// Relative imports (not `@/`) so bun can test this module.
import type { RuleSet } from '../rules/schema';
import {
  buildingLevel,
  isCity,
  type PlayerId,
  playerStock,
  type SimState,
  unitAmounts,
} from '../sim';

/** How each resource reads in the HUD, costs, badges and notifications. */
export const RESOURCE_ICONS: Readonly<Record<string, string>> = {
  food: '🌾',
  wood: '🪵',
  metal: '⛏️',
};

/**
 * `playerId`'s stock in whole units, as the HUD and the reason texts show and
 * compare it with rules costs (the sim keeps thousandths, sim/fixed.ts).
 */
export function stockInUnits(state: SimState, playerId: PlayerId): Record<string, number> {
  return unitAmounts(playerStock(state, playerId));
}

/** Display order: known resources first, the rest by id. */
const ORDER = Object.keys(RESOURCE_ICONS);

export function resourceIcon(res: string): string {
  return RESOURCE_ICONS[res] ?? res;
}

/** The resource ids of `amounts` in display order. */
export function orderedResources(amounts: Readonly<Record<string, number>>): string[] {
  const rank = (res: string) => {
    const i = ORDER.indexOf(res);
    return i === -1 ? ORDER.length : i;
  };
  return Object.keys(amounts).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * "12 🌾 3 🪵" for the whole units of an amount map, zeros left out ('' when
 * none). `compact` writes costs the way buttons show them: "90🌾 180🪵".
 */
export function formatAmounts(
  amounts: Readonly<Record<string, number>>,
  { compact = false }: { compact?: boolean } = {}
): string {
  return orderedResources(amounts)
    .filter((res) => Math.floor(amounts[res] ?? 0) > 0)
    .map((res) => {
      const n = Math.floor(amounts[res] ?? 0);
      return compact ? `${n}${resourceIcon(res)}` : `${n} ${resourceIcon(res)}`;
    })
    .join(' ');
}

/** One resource of a cost: whole units asked for, and how many the stock lacks. */
export interface CostPart {
  res: string;
  amount: number;
  short: number;
}

/**
 * A cost's resources in display order, zeros left out, each with the
 * shortfall against `stock` (whole units; none given means nothing is short).
 */
export function costParts(
  cost: Readonly<Record<string, number>>,
  stock?: Readonly<Record<string, number>>
): CostPart[] {
  return orderedResources(cost).flatMap((res) => {
    const amount = Math.floor(cost[res] ?? 0);
    if (amount <= 0) return [];
    const short = stock ? Math.max(0, amount - Math.floor(stock[res] ?? 0)) : 0;
    return [{ res, amount, short }];
  });
}

/** "short 12 wood + 3 food": the shortfall in words, null when nothing is short. */
export function shortfallText(parts: readonly CostPart[]): string | null {
  const short = parts.filter((p) => p.short > 0).map((p) => `${p.short} ${p.res}`);
  return short.length > 0 ? `short ${short.join(' + ')}` : null;
}

/** The city level whose era brings metal (the lowest a metal source is placed at). */
export function metalCityLevel(rules: RuleSet): number {
  let level = Number.POSITIVE_INFINITY;
  for (const def of Object.values(rules.buildings)) {
    if (def.provides?.resource === 'metal') level = Math.min(level, def.placement?.cityLevel ?? 1);
  }
  return level;
}

/**
 * The resources the HUD shows for a player: food and wood always, metal once
 * they hold some or a city of theirs has reached the era that brings it.
 */
export function shownResources(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  stock: Readonly<Record<string, number>>
): string[] {
  const shown = ['food', 'wood'];
  const unlock = metalCityLevel(rules);
  if (
    (stock.metal ?? 0) >= 1 ||
    Object.values(state.buildings).some(
      (b) =>
        b.owner === playerId &&
        isCity(b, rules) &&
        b.buildProgress >= 1 &&
        buildingLevel(b) >= unlock
    )
  ) {
    shown.push('metal');
  }
  return shown;
}

/** The resources pill's tooltip: one pool, whatever city holds it. */
export const RESOURCES_SHARED_TEXT =
  'Shared by all your cities: anything can be bought in any city, paid from whichever holds it. Per-city stock only matters if a city falls (what it held is lost, part of it plundered)';
